import type { AmbassadorBootstrap } from '@wix/ambassador-bootstrap-plugin';
import type { IHttpClient } from '@wix/http-client';
import type { QueryClient } from '@tanstack/react-query';
import { isSSR, createCacheKey } from '../utils';
import { HttpServices } from './http-services';
import { RpcServices } from './rpc-services';
import type { queryTag } from './tags';
import { Tags } from './tags';
import type { completeSetupCount } from './managed-apps';
import { MarketAppService } from './market-app';
import { FeatureToggleService } from './feature-toggle';
import { AppsService } from './apps-service';
import type { getAppsDataByTagId, queryTagApp } from './tag-apps';
import { APPS_LIMIT, TagApps, TagAppsService } from './tag-apps';
import type {
  getAppsByAppIds,
  getDynamicSections,
  getEligibleAppsForFreeTrial,
  Status,
} from './app-market-client-aggregator/types';
import {
  AppMarketClientAggregatorService,
  AppsData,
} from './app-market-client-aggregator';
import type { getWebSolutionsBase } from './web-solutions';
import { WebSolutionsBase } from './web-solutions';
import { type completePendingInstallation } from './pending-installations';
import type { PendingReason } from '@wix/ambassador-devcenter-pendingapps-v1-pending-installation/types';
import type { ComponentType as AppComponentsComponentType } from '@wix/ambassador-devcenter-app-components-v1-app-components/types';
import type { ComponentType as AppRuntimeComponentType } from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types';
import type {
  getAppComponentsByAppIds,
  getAppsRuntimeData,
} from './apps-components';
import { AppsComponents } from './apps-components';
import { AppsRuntimeComponents } from './apps-components/apps-runtime-components';
import type { getTagSections } from './tag-sections';
import type { getSiteProperties } from './site-properties';
import { SiteProperties } from './site-properties';
import {
  type getSitesPremiumStatus,
  SitesPremiumStatus,
} from './premium-data-view-retriever';
import type { getAutoCompleteSuggestions } from './app-market-search';
import { AutoComplete } from './app-market-search';
import {
  AutoComplete as AutoCompleteProxy,
  type getAutoCompleteProxySuggestions,
} from './app-market-search-proxy';
import {
  BundleApps,
  BundleAppsService,
  type getBundleApps,
} from './bundle-apps';
import type { getFreeTrialEligibility } from './premium-store';
import type { GetFreeTrialEligibilityRequest } from '@wix/ambassador-premium-store-v1-dynamic-offering-service-entity/types';
import { PlansService } from './plans';
import { AppsPlansService } from './apps-plans';
import { MarketListingService } from './market-listing';
import { GoogleAnalyticsService } from './google-analytics';
import { AppReviewsSummaryService } from './app-review-summary';
import { MyAppsService } from './my-apps/my-apps.service';
import { ReviewsService } from './review/reviews.service';
import { GetShareUrlLinkService } from './dev-center-share-url';
import { MarketSectionsService } from './market-sections';
import { HostListingsService } from './host-listings';
import { ConnectorService } from './connector';
import { TraceableTraderService } from './traceable-trader';
import type { getAppPermissions } from './app-permissions';
import { PremiumAssetsService } from './premium-assets/premium-assets.service';
import { SearchAppsService } from './app-market-search/search-apps.service';
import { SiteBranchesService } from './site-branches';
import type { getSignedAppMarketParams } from './signed-app-market-params';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import type { PostSupportEmailPayload } from '../types';
import { ManagedAppsService } from './managed-apps';
import { DealerServingOfferService } from './dealer-serving-offer';
import { DealerOfferEventService } from './dealer-offer-event';

/**
 * Implements IServices.
 ** abstracts SSR/CSR api calls.
 ** api response returns the abstracted domain model.
 For example, getManagedApps returns ManagedApps model.
 * @class Services
 */
export class Services {
  private httpServices: HttpServices;
  private rpcServices?: RpcServices | undefined;
  private queryClient?: QueryClient;

  constructor({
    httpClient,
    rpcClient,
    queryClient,
    errorHandler,
  }: {
    httpClient?: IHttpClient;
    rpcClient?: AmbassadorBootstrap;
    queryClient?: QueryClient;
    errorHandler?: ErrorHandlerPublicAPI;
  }) {
    if (!isSSR() && !httpClient) {
      throw new Error('httpServices is not defined');
    }
    this.httpServices = new HttpServices(httpClient!, errorHandler);
    this.rpcServices = isSSR() ? new RpcServices(rpcClient!) : undefined;
    this.queryClient = queryClient;
    this.marketListing = new MarketListingService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.plans = new PlansService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.appPlans = new AppsPlansService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.reviews = new ReviewsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.myApps = new MyAppsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.appReviewsSummary = new AppReviewsSummaryService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.devCenterShareUrl = new GetShareUrlLinkService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.marketApp = new MarketAppService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.featureToggle = new FeatureToggleService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.marketSections = new MarketSectionsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.googleAnalytics = new GoogleAnalyticsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.tagApps = new TagAppsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.bundleApps = new BundleAppsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.apps = new AppsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.traceableTrader = new TraceableTraderService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.premiumAssets = new PremiumAssetsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.searchApps = new SearchAppsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.siteBranches = new SiteBranchesService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.appMarketClientAggregator = new AppMarketClientAggregatorService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.managedApps = new ManagedAppsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.dealerServingOffer = new DealerServingOfferService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.dealerOfferEvent = new DealerOfferEventService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.hostListings = new HostListingsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.connector = new ConnectorService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
  }

  public marketListing: MarketListingService;
  public plans: PlansService;
  public appPlans: AppsPlansService;
  public reviews: ReviewsService;
  public myApps: MyAppsService;
  public appReviewsSummary: AppReviewsSummaryService;
  public devCenterShareUrl: GetShareUrlLinkService;
  public marketApp: MarketAppService;
  public featureToggle: FeatureToggleService;
  public marketSections: MarketSectionsService;
  public googleAnalytics: GoogleAnalyticsService;
  public tagApps: TagAppsService;
  public bundleApps: BundleAppsService;
  public apps: AppsService;
  public traceableTrader: TraceableTraderService;
  public premiumAssets: PremiumAssetsService;
  public searchApps: SearchAppsService;
  public siteBranches: SiteBranchesService;
  public appMarketClientAggregator: AppMarketClientAggregatorService;
  public managedApps: ManagedAppsService;
  public dealerServingOffer: DealerServingOfferService;
  public dealerOfferEvent: DealerOfferEventService;
  public hostListings: HostListingsService;
  public connector: ConnectorService;

  private async cachedRequest<TResponse>(
    methodName: string,
    payload: unknown,
    requestFn: () => Promise<TResponse>,
  ): Promise<TResponse> {
    if (!isSSR() && this.queryClient) {
      const serviceMethodName = `${this.constructor.name}.${methodName}`;
      const cacheKey = createCacheKey(serviceMethodName, payload);

      return this.queryClient.fetchQuery({
        queryKey: cacheKey,
        queryFn: requestFn,
      });
    }

    return requestFn();
  }

  getBundleApps: getBundleApps = async () => {
    return this.cachedRequest('getBundleApps', {}, async () => {
      if (this.rpcServices) {
        const response = await this.rpcServices?.getBundleApps({});
        return new BundleApps(response?.bundleApps?.bundleWebSolutions || []);
      } else {
        throw new Error(
          "getBundleApps doesn't support HttpService, please use RPCservice.",
        );
      }
    });
  };

  completeSetupCount: completeSetupCount = async (payload) => {
    return this.cachedRequest('completeSetupCount', payload, async () => {
      const response = this.rpcServices
        ? await this.rpcServices?.completeSetupCount(payload)
        : (await this.httpServices.completeSetupCount(payload)).data;

      return response.amountOfApps ?? 0;
    });
  };

  queryTag: queryTag = async (payload) => {
    return this.cachedRequest('queryTag', payload, async () => {
      const {
        tagIds: id,
        slugs: slug,
        languageCode,
        isHidden,
        tagType,
      } = payload;

      const req = {
        query: {
          filter: {
            ...(id ? { id } : {}),
            ...(slug ? { slug } : {}),
            ...(isHidden !== undefined ? { isHidden } : {}),
            ...(tagType ? { tagType } : {}),
          },
        },
        languageCode,
      };
      const response = this.rpcServices
        ? await this.rpcServices?.queryTag(req)
        : (await this.httpServices.queryTag(req)).data;

      return new Tags(response.tags || []);
    });
  };

  queryTagApp: queryTagApp = async (payload) => {
    return this.cachedRequest('queryTagApp', payload, async () => {
      const { appIds: appId, tagIds: tagId, skipRollout = false } = payload;
      const req = {
        query: {
          filter: {
            ...(appId ? { appId } : {}),
            ...(tagId ? { tagId } : {}),
          },
        },
        skipRollout,
      };

      const response = this.rpcServices
        ? await this.rpcServices?.queryTagApp(req)
        : (await this.httpServices.queryTagApp(req)).data;

      return new TagApps(response.tagApps || []);
    });
  };

  getAppsByAppIds: getAppsByAppIds = async (payload) => {
    if (payload.appIds.length === 0) {
      return new AppsData([]);
    }

    return this.cachedRequest('getAppsByAppIds', payload, async () => {
      const { appIds, status } = payload;
      const req = {
        appIds,
        status: status as Status,
      };

      return this.appMarketClientAggregator.getAppsByAppIds(req);
    });
  };

  getDynamicSections: getDynamicSections = async (payload) => {
    return this.cachedRequest('getDynamicSections', payload, async () =>
      this.appMarketClientAggregator.getDynamicSections(payload),
    );
  };

  getWebSolutionsBase: getWebSolutionsBase = async (payload) => {
    return this.cachedRequest('getWebSolutionsBase', payload, async () => {
      const req = {
        idsOrSlugs: payload.appIds.join(','),
      };
      const response = this.rpcServices
        ? await this.rpcServices?.getWebSolutionsBase(req)
        : (await this.httpServices.getWebSolutionsBase(req)).data;

      return new WebSolutionsBase(response.webSolutionsBase || []);
    });
  };

  completePendingInstallation: completePendingInstallation = async (
    payload,
  ) => {
    const req = {
      appId: payload.appId,
      pendingReason: payload.pendingReason as PendingReason,
    };

    this.rpcServices
      ? this.rpcServices?.completePendingInstallation(req)
      : this.httpServices.completePendingInstallation(req);
  };

  getTagSections: getTagSections = async (payload) => {
    if (!payload.tagIds.length) {
      throw new Error('tagIds empty in getTagSections req');
    }

    return this.cachedRequest('getTagSections', payload, async () => {
      return this.appMarketClientAggregator.getTagSections(payload);
    });
  };

  getSiteProperties: getSiteProperties = async () => {
    return this.cachedRequest('getSiteProperties', {}, async () => {
      if (this.rpcServices) {
        throw new Error("getSiteProperties doesn't support RPC.");
      }
      const response = await this.httpServices.getSiteProperties();
      return new SiteProperties(response?.properties);
    });
  };

  getSitesPremiumStatus: getSitesPremiumStatus = async (payload) => {
    return this.cachedRequest('getSitesPremiumStatus', payload, async () => {
      const response = this.rpcServices
        ? await this.rpcServices?.getSitesPremiumStatus(payload)
        : (await this.httpServices.getSitesPremiumStatus(payload)).data;

      return new SitesPremiumStatus(response.sitesPremiumStatus || []);
    });
  };

  getAppsDataByTagId: getAppsDataByTagId = async (payload) => {
    return this.cachedRequest('getAppsDataByTagId', payload, async () => {
      const { tagId, sortTypes, limit, offset = 0, status, filter } = payload;
      const tagApps = await this.tagApps.getAppsByTag({
        tagId,
        sortTypes,
        paging: {
          limit: limit && limit < APPS_LIMIT ? limit : APPS_LIMIT,
          offset,
        },
        ...(filter ? { filter } : {}),
      });
      const appsData = await this.getAppsByAppIds({
        appIds: tagApps.appIds,
        status,
      });
      return {
        apps: appsData.toJSON(),
        hasNext: tagApps.paging.hasNext,
        total: tagApps.paging.total,
      };
    });
  };

  getAppComponentsByAppIds: getAppComponentsByAppIds = async (payload) => {
    return this.cachedRequest('getAppComponentsByAppIds', payload, async () => {
      const { appId, componentTypes } = payload;
      const getAppComponentsByAppIdsRequest = {
        apps: [
          {
            appId,
          },
        ],
        componentTypes: componentTypes as AppComponentsComponentType[],
      };
      const response = this.rpcServices
        ? await this.rpcServices?.getAppComponentsByAppIds(
            getAppComponentsByAppIdsRequest,
          )
        : (
            await this.httpServices.getAppComponentsByAppIds(
              getAppComponentsByAppIdsRequest,
            )
          ).data;

      return new AppsComponents(response.appComponents || []);
    });
  };

  getAppsRuntimeData: getAppsRuntimeData = async (payload) => {
    return this.cachedRequest('getAppsRuntimeData', payload, async () => {
      const { appId, version, componentTypes } = payload;
      const getAppsRuntimeDataRequest = {
        appRuntimeKeys: [
          {
            appId,
            version,
          },
        ],
        componentTypes: componentTypes as AppRuntimeComponentType[],
      };
      const response = this.rpcServices
        ? await this.rpcServices?.getAppsRuntimeData(getAppsRuntimeDataRequest)
        : (
            await this.httpServices.getAppsRuntimeData(
              getAppsRuntimeDataRequest,
            )
          ).data;

      return new AppsRuntimeComponents(response.appsRuntimeData || []);
    });
  };

  getAutoCompleteProxySuggestions: getAutoCompleteProxySuggestions = async (
    payload,
  ) => {
    return this.cachedRequest(
      'getAutoCompleteProxySuggestions',
      payload,
      async () => {
        if (this.rpcServices) {
          throw new Error(
            "getAutoCompleteProxySuggestions doesn't support RPC.",
          );
        }
        const { searchTerm, languageCode } = payload;

        const response =
          await this.httpServices.getAutoCompleteProxySuggestions({
            searchTerm,
            languageCode: languageCode ?? 'en',
          });
        return new AutoCompleteProxy(response);
      },
    );
  };

  getAutoCompleteSuggestions: getAutoCompleteSuggestions = async (payload) => {
    return this.cachedRequest(
      'getAutoCompleteSuggestions',
      payload,
      async () => {
        const { searchTerm, paging } = payload;
        const getAutoCompleteSuggestionsRequest = {
          searchTerm,
          paging,
        };
        const response = this.rpcServices
          ? await this.rpcServices?.getAutoCompleteSuggestions(
              getAutoCompleteSuggestionsRequest,
            )
          : (
              await this.httpServices.getAutoCompleteSuggestions(
                getAutoCompleteSuggestionsRequest,
              )
            ).data;

        return new AutoComplete(response.suggestions || []);
      },
    );
  };

  getFreeTrialEligibility: getFreeTrialEligibility = async (payload) => {
    return this.cachedRequest('getFreeTrialEligibility', payload, async () => {
      const getFreeTrialEligibilityRequest: GetFreeTrialEligibilityRequest = {
        productTypeId: '09ffaddc-9188-46f1-a8ad-33402da0907d',
        productFamilyId: payload.appId,
      };
      const response = this.rpcServices
        ? await this.rpcServices?.getFreeTrialEligibility(
            getFreeTrialEligibilityRequest,
          )
        : (
            await this.httpServices.getFreeTrialEligibility(
              getFreeTrialEligibilityRequest,
            )
          ).data;

      return !!response.freeTrialEligibility?.eligible;
    });
  };

  getEligibleAppsForFreeTrial: getEligibleAppsForFreeTrial = async () => {
    return this.cachedRequest('getEligibleAppsForFreeTrial', {}, async () => {
      const { eligibleAppIds } = this.rpcServices
        ? await this.rpcServices?.getEligibleAppsForFreeTrial({})
        : (await this.httpServices.getEligibleAppsForFreeTrial({})).data;

      return eligibleAppIds ?? [];
    });
  };

  getAppPermissions: getAppPermissions = async (payload) => {
    return this.cachedRequest('getAppPermissions', payload, async () => {
      if (this.rpcServices) {
        throw new Error("getSiteProperties doesn't support RPC.");
      }

      return this.httpServices.getAppPermissions(payload);
    });
  };

  getSignedAppMarketParams: getSignedAppMarketParams = async (payload) => {
    return this.cachedRequest('getSignedAppMarketParams', payload, async () => {
      if (this.rpcServices) {
        throw new Error("getSignedAppMarketParams doesn't support RPC.");
      }

      return this.httpServices.getSignedAppMarketParams(payload);
    });
  };

  postSupportEmail: PostSupportEmailPayload = async (payload) => {
    return this.cachedRequest('postSupportEmail', payload, async () => {
      return this.httpServices.postSupportEmail(payload);
    });
  };
}
